export default function MinnetonkaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Minnetonka Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Minnetonka travelers actually move helps us anticipate the passport urgencies that arise here. From Fortune 500 corporate travel to lakeside leisure and Canadian cabin trips, western metro travel operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* MSP Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 18 miles from Minnetonka — 25 minutes by car</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Minnetonka is well-positioned for MSP access via I-394 and Highway 7. With nonstop service to Amsterdam, London, Paris, Tokyo, and Reykjavik, many residents connect directly to Europe and Asia. The airport's location means a Minnetonka executive can check their passport status on Monday morning and be on a transatlantic flight by Thursday evening.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Because MSP is so accessible, many Minnetonka travelers book international flights with minimal lead time. Check your passport expiration before every travel planning conversation, not after tickets are purchased.
              </p>
            </div>
          </div>

          {/* Corporate & Fortune 500 Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate &amp; Fortune 500 Travel</h3>
                <p className="text-gray-400 text-xs">UnitedHealth Group, Cargill, and international headquarters</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Minnetonka is home to some of Minnesota's largest corporate headquarters. Executives at UnitedHealth Group, Cargill, and other Fortune 500 companies frequently receive international travel assignments with 10–21 days notice. When a passport is discovered to be expired, standard processing is not fast enough. Corporate travel demands predictable, professional service that matches the urgency of the business.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Corporate travelers should check passport expiration quarterly. Set calendar reminders for the entire team. Business travel is rarely planned months in advance.
              </p>
            </div>
          </div>

          {/* Lake Minnetonka & Leisure Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Lake Minnetonka &amp; Leisure Travel</h3>
                <p className="text-gray-400 text-xs">Summer cruises, Caribbean vacations, and European tours</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Minnetonka's affluent community includes frequent leisure travelers. European tours, Caribbean cruises, and luxury resort trips are common. Many residents book through travel agencies with 3–6 weeks notice, leaving insufficient time for standard government processing. The lakeside lifestyle often extends to international waterfront destinations.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Book your passport processing at the same time as your trip. If your passport expires within 9 months, consider renewing before booking.
              </p>
            </div>
          </div>

          {/* Canada & Northern Cabin Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-tent-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Canada &amp; Northern Cabin Travel</h3>
                <p className="text-gray-400 text-xs">Ontario fishing, Manitoba hunting, and Canadian lake vacations</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Minnetonka residents maintain cabins and vacation properties in Canada. Summer travel to Ontario lakes and fall hunting trips to Manitoba require current passports for all travelers. Children's passports are only valid for 5 years. Canada travel requires passports for all family members, including children.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Canada travel requires passports for all family members. Even children need passports for international travel by air. Verify all documents before summer cabin season.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corporate Event &amp; Conference Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Minnetonka's professional community hosts and attends major industry conferences and events that draw international attendees. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Minnetonka professionals traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}